/* eslint-disable */
import {
    types as ActionTypes,
    asyncEvents as AsyncEvents,
    contextEvents as ContextEvents,
    mfaEvents as MfaEvents,
} from './Auth.actions';
import amplify, { initialState as initialAmplify } from './amplify/Amplify.reducer';
import customer, { initialState as initialCustomer } from './customer/AuthCustomer.reducer';
import {
    genericReducer,
    loadingReducer,
    passthroughReducer,
} from '../../common/helpers/ReduxHelpers';
import { getAuthState, getSignInStep } from './amplify/Amplify.selectors';
import user, { initialState as initialUser } from './user/AuthUser.reducer';

import { AUTH_STATES } from '../constants';
import AmplifyActions from './amplify/Amplify.actions';
import { DEFAULT_MFA_OPTIONS } from './amplify/Amplify.constants';
import { types as NavActionTypes } from '../../common/state/nav/Nav.actions';
import { combineReducers } from 'redux';
import { filterUnique } from '/b2b/common/helpers/Array';
import reduceReducers from 'reduce-reducers';

export const initialState = {
    amplify: initialAmplify,
    customer: initialCustomer,
    user: initialUser,
    authState: AUTH_STATES.signedOut,
    error: null,
    mfaType: '',
    mfaOptions: [],
    loading: false,
    fetchingContext: false,
    fetchingMfaOptions: null,
    resentCode: null,
    customerToken: null,
    rememberMe: false,
    ssoEnabled: true,
    sessionToken: null,
};

export const defaultError = 'An error occurred during authorization';

export default reduceReducers(
    initialState,
    combineReducers({
        amplify,
        user,
        customer,
        customerToken: genericReducer(initialState.customerToken, {
            [ActionTypes.signInSuccess]: () => initialState.customerToken,
        }),
        fetchingContext: loadingReducer(
            initialState.fetchingContext,
            Object.keys(ContextEvents).map(key => ActionTypes[key])
        ),
        fetchingMfaOptions: loadingReducer(
            initialState.fetchingMfaOptions,
            Object.keys(MfaEvents).map(key => ActionTypes[key])
        ),
        mfaOptions: genericReducer(initialState.mfaOptions, {
            [ActionTypes.getMfaOptionsSuccess]: (state, { response: mfaOptions }) =>
                mfaOptions
                    .filter(filterUnique)
                    .filter(value => DEFAULT_MFA_OPTIONS.includes(value))
                    .sort((a, b) => {
                        return DEFAULT_MFA_OPTIONS.indexOf(a) - DEFAULT_MFA_OPTIONS.indexOf(b);
                    }),
            [ActionTypes.removeMfa]: (state, mfaToRemove) =>
                [...state].filter(value => value !== mfaToRemove.toUpperCase()),
            [AmplifyActions.nextStep]: (state, payload) => {
                const { signInStep = 'SIGN_IN', allowedMFATypes } = payload?.nextStep;
                const newMfaOptions = [...state];
                switch (signInStep) {
                    case 'CONFIRM_SIGN_IN_WITH_SMS_CODE': {
                        newMfaOptions.push('SMS');
                        break;
                    }
                    case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
                    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE': {
                        newMfaOptions.push('TOTP');
                        break;
                    }
                    case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE': {
                        newMfaOptions.push('EMAIL');
                        break;
                    }
                    case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION': {
                        newMfaOptions.push(...allowedMFATypes);
                        break;
                    }
                }
                return newMfaOptions
                    .filter(filterUnique)
                    .filter(value => DEFAULT_MFA_OPTIONS.includes(value))
                    .sort((a, b) => {
                        return DEFAULT_MFA_OPTIONS.indexOf(a) - DEFAULT_MFA_OPTIONS.indexOf(b);
                    });
            },
            [ActionTypes.addMfaSuccess]: (state, { params: { mfaType: providedMFA } = {} }) => {
                return [providedMFA.toUpperCase(), ...state]
                    .filter(filterUnique)
                    .filter(value => DEFAULT_MFA_OPTIONS.includes(value))
                    .sort((a, b) => {
                        return DEFAULT_MFA_OPTIONS.indexOf(a) - DEFAULT_MFA_OPTIONS.indexOf(b);
                    });
            },
        }),
        mfaType: genericReducer(initialState.mfaType, {
            [ActionTypes.selectMfa]: (state, mfaType) => {
                switch (mfaType) {
                    case 'SMS':
                    case 'SMS_MFA': {
                        return 'SMS';
                    }
                    case 'TOTP':
                    case 'SOFTWARE_TOKEN_MFA': {
                        return 'TOTP';
                    }
                    case 'SELECT_MFA_TYPE': {
                        return 'SELECT_MFA_TYPE';
                    }
                }
                return 'NOMFA';
            },
            [AmplifyActions.nextStep]: (state, payload) => {
                const { signInStep = 'SIGN_IN', allowedMFATypes } = payload?.nextStep;
                switch (signInStep) {
                    case 'CONFIRM_SIGN_IN_WITH_SMS_CODE': {
                        return 'SMS';
                    }
                    case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
                    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE': {
                        return 'TOTP';
                    }
                    case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE': {
                        return 'EMAIL';
                    }
                }
                return state;
            },
            [ActionTypes.setPreferredMfaSuccess]: (state, { params: mfaType }) => {
                switch (mfaType) {
                    case 'SMS':
                    case 'SMS_MFA': {
                        return 'SMS';
                    }
                    case 'TOTP':
                    case 'SOFTWARE_TOKEN_MFA': {
                        return 'TOTP';
                    }
                }
                return 'NOMFA';
            },
        }),
        rememberMe: genericReducer(initialState.rememberMe, {
            [ActionTypes.signInBegin]: (state, { params: { rememberMe = state } = {} } = {}) =>
                rememberMe,
            [ActionTypes.fetchSignInContextBegin]: (
                state,
                { params: { rememberMe = state } = {} } = {}
            ) => rememberMe,
        }),
        resentCode: genericReducer(initialState.resentCode, {
            [ActionTypes.signInSuccess]: () => null,
            [ActionTypes.confirmSignInSuccess]: () => null,
            [ActionTypes.resendSignUpCodeBegin]: () => null,
            [ActionTypes.resendSignUpCodeSuccess]: () => true,
            [ActionTypes.resendSignUpCodeFailure]: (
                state,
                { error: { message = defaultError } = {} }
            ) => message,
            [ActionTypes.resendEmailVerificationCodeBegin]: () => null,
            [ActionTypes.resendEmailVerificationCodeSuccess]: () => true,
            [ActionTypes.resendEmailVerificationCodeFailure]: (
                state,
                { error: { message = defaultError } = {} }
            ) => message,
            [ActionTypes.resendPhoneVerificationCodeBegin]: () => null,
            [ActionTypes.resendPhoneVerificationCodeSuccess]: () => true,
            [ActionTypes.resendPhoneVerificationCodeFailure]: (
                state,
                { error: { message = defaultError } = {} }
            ) => message,
        }),
        authState: genericReducer(initialState.authState, {
            [ActionTypes.updateAuthState]: (state, newAuthState) =>
                Object.values(AUTH_STATES).includes(newAuthState) ? newAuthState : state,
            [ActionTypes.confirmSignInSuccess]: (state, { response }) => {
                const authUser = /** @type {import('aws-amplify/auth').AuthUser} */ (response);
                if (authUser.userId) {
                    return AUTH_STATES.signedIn;
                }
                return state;
            },
            [ActionTypes.setPreferredMfaSuccess]: () => AUTH_STATES.signedIn,
            [ActionTypes.selectMfa]: (state, mfaType) => {
                switch (mfaType) {
                    case 'NOMFA': {
                        return AUTH_STATES.selectMFA;
                    }
                }
                return state;
            },
            [ActionTypes.confirmSignUpSuccess]: () => AUTH_STATES.selectMFA,
            [ActionTypes.verifyEmailSuccess]: () => {
                return AUTH_STATES.signedIn;
            },
            [ActionTypes.verifyPhoneSuccess]: () => {
                return AUTH_STATES.signedIn;
            },
            [ActionTypes.resetPasswordFailure]: () => AUTH_STATES.forgotPassword, // No NextStep
            [ActionTypes.forgotPasswordSuccess]: (state, { params, response }) => {
                const {
                    isPasswordReset,
                    nextStep: {
                        resetPasswordStep, // 'DONE' | 'CONFIRM_PASSWORD_RESET_WITH_CODE'
                        additionalInfo = {},
                        codeDeliveryDetails: {
                            attributeName = 'email', // 'email' | 'phone_number'
                            deliveryMedium = 'email', // 'SMS' | 'EMAIL' | 'PHONE' | 'UNKNOWN'
                            destination = params.userId || params.username || null,
                        },
                    },
                } = /** @type {import('aws-amplify/auth').ResetPasswordOutput} */ response;
                if (isPasswordReset) {
                    switch (resetPasswordStep) {
                        case 'DONE':
                            return AUTH_STATES.forgotPassword;
                        case 'CONFIRM_PASSWORD_RESET_WITH_CODE':
                            return AUTH_STATES.confirmPasswordReset;
                    }
                }
                return AUTH_STATES.forgotPassword;
            },
            [ActionTypes.completeNewPasswordFailure]: () => AUTH_STATES.requireNewPassword,
            [ActionTypes.signInFailure]: (state, { error }) => {
                if (error.name === 'UserNotConfirmedException') {
                    // The error happens if the user didn't finish the confirmation step when signing up
                    // In this case you need to resend the code and confirm the user
                    // About how to resend the code and confirm the user, please check the signUp part

                    // TODO: Check email or phone
                    return AUTH_STATES.confirmEmail;
                } else if (error.name === 'PasswordResetRequiredException') {
                    // The error happens when the password is reset in the Cognito console
                    // In this case you need to call forgotPassword to reset the password
                    // Please check the Forgot Password part.
                    return AUTH_STATES.forgotPassword;
                } else if (error.name === 'NotAuthorizedException') {
                    // The error happens when the incorrect password is provided, or a temporary password expired
                    if (error.message.indexOf('Temporary password has expired') >= 0) {
                        // Resend the password
                        return AUTH_STATES.resendInvitation;
                    }
                    return AUTH_STATES.signedOut;
                } else if (error.name === 'UserNotFoundException') {
                    // The error happens when the supplied username/email does not exist in the Cognito user pool
                    return AUTH_STATES.signedOut;
                }
                return AUTH_STATES.signedOut;
            },
            [ActionTypes.signUpFailure]: () => AUTH_STATES.signedOut,
            [ActionTypes.signUpSuccess]: (state, { response: { userConfirmed } = {} }) => {
                if (!userConfirmed) {
                    // TODO: Figure out verification for email or phone
                    return AUTH_STATES.confirmEmail;
                }
                return AUTH_STATES.signedIn;
            },
        }),
        error: genericReducer(initialState.error, {
            [ActionTypes.signInFailure]: (state, { error: { message = defaultError } = {} }) =>
                message,
            [ActionTypes.signInSuccess]: () => null,
            [ActionTypes.signUpFailure]: (state, { error: { message = defaultError } = {} }) =>
                message,
            [ActionTypes.signUpSuccess]: () => null,
            [ActionTypes.confirmSignInFailure]: (
                state,
                { error: { message = defaultError } = {} }
            ) => message,
            [ActionTypes.confirmSignInSuccess]: () => null,
            [ActionTypes.confirmEmailFailure]: (
                state,
                { error: { message = defaultError } = {} }
            ) => message,
            [ActionTypes.confirmPhoneFailure]: (
                state,
                { error: { message = defaultError } = {} }
            ) => message,
            [ActionTypes.confirmEmailSuccess]: () => null,
            [ActionTypes.confirmPhoneSuccess]: () => null,
            [ActionTypes.resetPasswordFailure]: (
                state,
                { error: { message = defaultError } = {} }
            ) => message,
            [ActionTypes.resetPasswordSuccess]: () => null,
            [ActionTypes.completeNewPasswordFailure]: (
                state,
                { error: { message = defaultError } = {} }
            ) => message,
            [ActionTypes.completeNewPasswordSuccess]: () => null,
            [ActionTypes.forgotPasswordFailure]: (
                state,
                { error: { message = defaultError } = {} }
            ) => message,
            [ActionTypes.forgotPasswordSuccess]: () => null,
        }),
        loading: loadingReducer(
            true,
            Object.keys(AsyncEvents).map(key => ActionTypes[key])
        ),
        ssoEnabled: genericReducer(initialState.ssoEnabled, {
            [ActionTypes.fetchSignInContextSuccess]: (state, { response }) => {
                const { identityProviderName, ssoEnabled } = response;
                return ssoEnabled;
            },
        }),
        sessionToken: genericReducer(initialState.sessionToken, {
            [ActionTypes.refreshTokens]: (
                state,
                /** @type {import('aws-amplify/auth').AuthSession['tokens']} */ tokens
            ) => {
                const { idToken } = tokens;
                return idToken?.toString() || null;
            },
            [ActionTypes.logout]: () => null,
        }),
    }),
    genericReducer(initialState, {
        [ActionTypes.logout]: state => {
            const { customer, rememberMe, user } = state;
            return {
                ...initialState,
                customer: {
                    ...initialState.customer,
                    id: rememberMe ? customer?.id : initialState.customer.id,
                },
                rememberMe,
                user: {
                    ...initialState.user,
                    id: rememberMe ? user?.id : initialState.user.id,
                },
            };
        },
        [NavActionTypes.changeCustomer]: (state, customer = {}) => {
            const { token } = customer;
            return {
                ...state,
                // role: lookupUserRole(role)[0],
                customerToken: token || null,
            };
        },
        [ActionTypes.signInSuccess]: (
            state,
            { params: { rememberMe = state.rememberMe } = {} } = {}
        ) => {
            return {
                ...state,
                rememberMe: Boolean(rememberMe),
            };
        },
    })
);
